import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { isContractsSymbol } from "@shared/utils/misc";
import usePricesSocket from "@shared/services/socket/usePricesSocket";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { getPricePrecision } from "@shared/features/portfolio/utils";
import { typography } from "@shared/common/Themes/typography";
import { SharedLiveSymbolPriceProps } from "@shared/services/socket/types";
import { PrecisionType } from "@shared/types";
import { useMemo } from "react";

const useLiveSymbolPrice = ({
  symbol,
  exchange,
  initialPrice,
  fontSize,
  colorValueChange,
  textColor,
}: SharedLiveSymbolPriceProps) => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { socketPrice, getPriceColor } = usePricesSocket(symbol, exchange);
  const price = socketPrice.value || initialPrice;

  let { precision } = usePrecisions(
    symbol && exchange
      ? {
          symbol,
          exchange,
          precisionType: PrecisionType.PRICE,
        }
      : undefined
  );
  if (!isContractsSymbol(symbol))
    precision = getPricePrecision(price || 0, displayCurrency);

  const props = useMemo(
    () => ({
      text: price,
      precision,
    }),
    [price, precision]
  );

  const color = colorValueChange ? getPriceColor(textColor) : textColor;
  const textStyle = useMemo(
    () => ({
      fontSize: fontSize || typography.pxFontSizes.M,
      color,
    }),
    [fontSize, color]
  );

  return { getPriceColor, props, textStyle };
};

export default useLiveSymbolPrice;
